import React from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import DropdownMenu from '../../components/dropdown-menu';
import {
  DrawerField as Field,
  DrawerItems,
  DrawerSection as Section,
  DrawerShell,
} from '../../components/detail-drawer';
import {
  formatWeight,
  getEntryDate,
  getTimeSlot,
  getOrderStatus,
  getTransportDate,
} from './orders-pool.utils';

/**
 * Fiche BL en lecture (quick-peek) : drawer de 620 px ouvert au clic sur une
 * ligne. Les données de la ligne s'affichent tout de suite, l'adresse
 * complète et le créneau arrivent avec GET /tours-planning/:id/details.
 */
export default function OrderDrawer({
  group,
  row,
  details,
  isLoadingDetails,
  menuItems,
  onClose,
}) {
  const status = getOrderStatus(group.element);
  // Pour le puits, la date de référence est l'entrée du BL dans le puits ;
  // la date de livraison du fichier reste affichée dans la section Dates
  const entryDate = getEntryDate(row.createdAt);
  const transportDate = getTransportDate(row.date);
  const scheduledAt = group.element?.scheduled_at;
  const address = details?.address;
  const step = details?.step;
  const phones = address?.phone_numbers || [];
  const quantity = row.items.reduce(
    (sum, item) => sum + (parseInt(item.quantity, 10) || 1),
    0
  );
  const hasCoordinates =
    address?.latitude !== undefined &&
    address?.latitude !== null &&
    address?.longitude !== undefined &&
    address?.longitude !== null;

  const handleCopyPhone = async () => {
    try {
      await navigator.clipboard.writeText(phones[0].phone_number);
      toast.success('Numéro de téléphone copié');
    } catch (error) {
      toast.error('Impossible de copier le numéro');
    }
  };

  const loadingHint = isLoadingDetails ? 'Chargement…' : null;

  return (
    <DrawerShell ariaLabel={`BL ${row.blKey}`} onClose={onClose}>
      <div className="agv-drawer__header">
        <div className="agv-drawer__title-row">
          <button
            type="button"
            className="agv-dg-icon-btn agv-dg-icon-btn--large"
            onClick={onClose}
            title="Retour à la liste"
          >
            ‹
          </button>
          <span className="agv-drawer__title">{row.blKey}</span>
          <span className="agv-dg-status agv-dg-status--strong">
            <span
              className="agv-dg-dot"
              style={{ backgroundColor: status.color }}
            />
            {status.label}
          </span>
          <span className="agv-dg-spacer" />
          {menuItems.length > 0 && (
            <DropdownMenu
              width={300}
              items={menuItems}
              renderTrigger={({ toggle }) => (
                <button
                  type="button"
                  className="agv-dg-ghost-btn agv-dg-ghost-btn--square"
                  onClick={toggle}
                  title="Actions"
                >
                  ···
                </button>
              )}
            />
          )}
          <button
            type="button"
            className="agv-dg-icon-btn"
            onClick={onClose}
            title="Fermer"
          >
            ✕
          </button>
        </div>
        {(row.isManual || group.plannedVehicle || group.isDeleted) && (
          <div className="agv-drawer__pills">
            {group.isDeleted && (
              <span className="agv-drawer-pill agv-drawer-pill--danger">
                {[
                  'Supprimé du puits',
                  group.archivedAt &&
                    `le ${moment
                      .utc(group.archivedAt)
                      .local()
                      .format('DD/MM/YYYY [à] HH:mm')}`,
                  group.archivedBy && `par ${group.archivedBy}`,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </span>
            )}
            {group.plannedVehicle && (
              <span
                className="agv-drawer-pill agv-drawer-pill--info"
                title="Camion prévu par le fichier : la commande lui est affectée d'office à l'envoi en ordonnancement"
              >
                Camion prévu : {group.plannedVehicle}
              </span>
            )}
            {row.isManual && (
              <span className="agv-drawer-pill">Création manuelle</span>
            )}
          </div>
        )}
        <span className="agv-drawer__origin">
          {group.recipient}
          {group.recipientCode && ` · code ${group.recipientCode}`}
          {entryDate.time &&
            ` · BL reçu le ${entryDate.label} à ${entryDate.time}`}
        </span>
      </div>

      <div className="agv-drawer__facts">
        <div className="agv-drawer-fact">
          <span className="agv-drawer-fact__label">Entrée puits</span>
          <span className="agv-drawer-fact__value">{entryDate.label}</span>
          {entryDate.time && (
            <span className="agv-drawer-fact__hint">à {entryDate.time}</span>
          )}
        </div>
        <div className="agv-drawer-fact">
          <span className="agv-drawer-fact__label">Poids</span>
          <span className="agv-drawer-fact__value">
            {formatWeight(row.weight)}
          </span>
        </div>
        <div className="agv-drawer-fact">
          <span className="agv-drawer-fact__label">Articles</span>
          <span className="agv-drawer-fact__value">{row.items.length}</span>
          <span className="agv-drawer-fact__hint">
            {quantity} unité{quantity > 1 ? 's' : ''}
          </span>
        </div>
        <div className="agv-drawer-fact">
          <span className="agv-drawer-fact__label">Réceptionnaire</span>
          <span className="agv-drawer-fact__value">{group.recipient}</span>
          <span className="agv-drawer-fact__hint">
            {[
              group.recipientCode,
              `${group.totalBlCount} BL pour ce réceptionnaire`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </div>

      <div className="agv-drawer__body">
        <Section
          title="RÉCEPTIONNAIRE & LIVRAISON"
          action={
            phones.length > 0 && (
              <button
                type="button"
                className="agv-dg-link"
                onClick={handleCopyPhone}
              >
                Copier le n° de téléphone
              </button>
            )
          }
        >
          <Field
            label="Réceptionnaire"
            value={group.recipient}
            hint={group.recipientCode && `Code ${group.recipientCode}`}
            valueClassName="agv-drawer-field__value--strong"
          />
          <Field
            label="Adresse de livraison"
            value={
              [
                [address?.address_number, address?.street || group.street]
                  .filter(Boolean)
                  .join(' '),
                [address?.zipcode, address?.city].filter(Boolean).join(' ') ||
                  group.city,
              ]
                .filter(Boolean)
                .join(', ') || '—'
            }
            hint={
              loadingHint ||
              (hasCoordinates
                ? `● géocodée · ${Number(address.latitude).toFixed(
                    3
                  )}, ${Number(address.longitude).toFixed(3)}`
                : 'adresse non géocodée')
            }
          />
          <Field
            label="Téléphones"
            value={
              phones.length > 0
                ? phones
                    .map((phone) =>
                      [phone.phone_number, phone.name && `(${phone.name})`]
                        .filter(Boolean)
                        .join(' ')
                    )
                    .join(' · ')
                : '—'
            }
            hint={loadingHint}
          />
          {address?.email && <Field label="Email" value={address.email} />}
          {address?.communication_preference && (
            <Field
              label="Préférence de contact"
              value={address.communication_preference}
            />
          )}
        </Section>

        <Section title="DATES">
          <Field
            label="Entrée dans le puits"
            value={
              entryDate.time
                ? `${entryDate.label} à ${entryDate.time}`
                : entryDate.label
            }
          />
          <Field
            label="Livraison (fichier)"
            value={transportDate.label}
            valueClassName={
              transportDate.tone ? `agv-dg-tone--${transportDate.tone}` : ''
            }
          />
          {step?.estimated_delivery && (
            <Field
              label="Livraison estimée"
              value={moment(step.estimated_delivery).format('DD/MM/YYYY')}
            />
          )}
          {getTimeSlot(step) && (
            <Field label="Créneau" value={getTimeSlot(step)} />
          )}
        </Section>

        <Section title="ORDONNANCEMENT">
          <Field
            label="Statut"
            value={
              <span className="agv-dg-status">
                <span
                  className="agv-dg-dot"
                  style={{ backgroundColor: status.color }}
                />
                {status.label}
              </span>
            }
          />
          <Field
            label="Affectation"
            value={
              scheduledAt
                ? `Ordonnancé pour le ${moment(scheduledAt).format('DD/MM/YYYY')}`
                : '—'
            }
            valueClassName={scheduledAt ? '' : 'agv-drawer-field__value--muted'}
            hint="Le camion, la séquence et l'heure estimée se consultent dans Affectations Camion une fois le BL envoyé en ordonnancement."
          />
        </Section>

        <Section
          title={`ARTICLES (${row.items.length})`}
          action={
            <span className="agv-drawer__section-meta">
              total {formatWeight(row.weight)}
            </span>
          }
        >
          <DrawerItems items={row.items} />
        </Section>

        {step?.comments && (
          <Section title="COMMENTAIRE DE COMMANDE  ·  lecture seule">
            <div className="agv-drawer-comment">
              <span>💬</span>
              <span>{step.comments}</span>
            </div>
          </Section>
        )}
      </div>

      <div className="agv-drawer__footer">
        <span className="agv-drawer__footer-note">
          Lecture seule. Les modifications faites dans le TMS ne sont jamais
          répercutées vers l&apos;ERP.
        </span>
        <button type="button" className="agv-dg-ghost-btn" onClick={onClose}>
          Fermer
        </button>
      </div>
    </DrawerShell>
  );
}
